import { useAtom, useAtomValue } from 'jotai';
import { destInfoModifyListAtom } from './DestInfo';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { PlanDest } from '@/types/plan';
import { Badge, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { masterDestListAtom, putCoordInfo, putDestInfoList } from '@/interfaces/plan/manage';
import * as _ from 'lodash';
import CoordMap from '@/components/exec/common/coordMap';
import { regionAtom } from '@/interfaces/region';
import { RegionGroup } from '@/types/regions';

const MainContentWrap = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  .single{
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 1rem;
  }
  .row{
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
  }
`;

export default function SingleMode() {
  const { data: regionList } = useAtomValue(regionAtom);
  const [modifyInfo, setModifyInfo] = useAtom(destInfoModifyListAtom);
  const { refetch } = useAtomValue(masterDestListAtom);
  const [single, setSingle] = useState<PlanDest>();
  const [workTime, setWorkTime] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [modifyFlag, setModifyFlag] = useState(false);

  useEffect(() => {
    if (modifyInfo && modifyInfo.length === 1) {
      setSingle(modifyInfo[0]);
    }
  }, [modifyInfo]);

  useEffect(() => {
    if (modifyInfo && modifyInfo[0]) {
      setModifyFlag(single !== modifyInfo[0]);
    }
  }, [single]);

  const handleSingleSaveClick = async () => {
    await putDestInfoList({ ...single, targetSeqList: [single.seq] });
    await putCoordInfo(single);
    refetch();
    setModifyInfo([single]);
  };

  const handleInitClick = () => {
    if (modifyInfo && modifyInfo.length === 1) {
      setSingle(modifyInfo[0]);
    }
  };

  const handleAddWorkTimeClick = () => {
    if (workTime?.start !== '' && workTime?.end !== '') {
      const temp = single;
      const workTemp = temp.work_time;
      const checkItem = workTemp.find((target) => {
        if (target.start < workTime.start && target.end > workTime.start) return target;
        if (target.start < workTime.end && target.end > workTime.end) return target;
      });
      !checkItem && workTemp.push(workTime);
      setSingle({ ...temp, work_time: _.orderBy(workTemp, ['start'], ['asc']) as [{ start: string; end: string }] });
      setWorkTime({ start: '', end: '' });
    }
  };

  const handleChangeWorkTime = (key: 'start' | 'end', val: string) => {
    const temp = { ...workTime };
    temp[key] = val;
    setWorkTime(temp);
  };

  const handleWorkTimeDelete = (index: number) => {
    const temp = [
      ...single.work_time.slice(0, index),
      ...single.work_time.slice(index + 1, single.work_time.length),
    ] as [{ start: string; end: string }];
    setSingle({ ...single, work_time: temp });
  };

  return (
    <MainContentWrap>
      {single && (
        <div className="single">
          <div className="row">
            <TextField
              label="배송지"
              size="small"
              sx={{ width: '18.6rem' }}
              slotProps={{ inputLabel: { shrink: true } }}
              value={single.name}
              onChange={(e) => setSingle({ ...single, name: e.target.value })}
            />
            <TextField
              label="거래처코드"
              size="small"
              sx={{ width: '12rem' }}
              slotProps={{ inputLabel: { shrink: true } }}
              value={single.customer_code ?? '-'}
              onChange={(e) => setSingle({ ...single, customer_code: e.target.value })}
            />
            <TextField
              label="주소"
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '40%' }}
              value={single.address}
              onChange={(e) => setSingle({ ...single, address: e.target.value })}
            />
            <Button
              variant="contained"
              disabled={!modifyFlag}
              color="secondary"
              size="medium"
              onClick={handleInitClick}
            >
              초기화
            </Button>
            <Button
              variant="contained"
              disabled={!modifyFlag}
              color="success"
              size="medium"
              onClick={handleSingleSaveClick}
            >
              저장
            </Button>
          </div>
          <div className="row">
            {regionList && (
              <FormControl size="small" sx={{ width: '10rem' }}>
                <InputLabel id="demo-simple-select-label">권역</InputLabel>
                <Select
                  size="small"
                  label="권역"
                  value={single.region ?? '-'}
                  onChange={(e) =>
                    setSingle({
                      ...single,
                      region: e.target.value,
                      region_group_seq: _.find(regionList, ['name', e.target.value])?.region_group_seq,
                    })
                  }
                >
                  <MenuItem value={'-'}>-</MenuItem>
                  {regionList.map((item: RegionGroup, index: number) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="체류시간"
              size="small"
              type="number"
              sx={{ width: '6rem' }}
              value={single.dwell_time / 60}
              onChange={(e) => setSingle({ ...single, dwell_time: Number(e.target.value) * 60 })}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  inputProps: { style: { textAlign: 'right' } },
                  endAdornment: <InputAdornment position="end">분</InputAdornment>,
                },
              }}
            />
            <TextField
              label="메모"
              // sx={{ width: '40%' }}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              value={single.memo}
              onChange={(e) => setSingle({ ...single, memo: e.target.value })}
            />
          </div>
          <div className="row">
            <TextField
              label="배송가능시간(시작)"
              size="small"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  inputProps: { style: { textAlign: 'right' } },
                },
              }}
              sx={{ width: '6rem' }}
              value={workTime?.start}
              onChange={(e) => handleChangeWorkTime('start', e.target.value)}
            />{' '}
            ~
            <TextField
              label="배송가능시간(종료)"
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '6rem' }}
              value={workTime?.end}
              onChange={(e) => handleChangeWorkTime('end', e.target.value)}
            />
            <Button variant="contained" onClick={handleAddWorkTimeClick}>
              추가
            </Button>
            {single.work_time &&
              single.work_time.length > 0 &&
              single.work_time.map((work, index) => (
                <Badge
                  key={index}
                  badgeContent={<CloseIcon onClick={() => handleWorkTimeDelete(index)} />}
                  color="error"
                >
                  <div
                    style={{
                      background: 'rgba(82, 133, 164, 0.87)',
                      borderRadius: '0.75rem',
                      padding: '6px 12px',
                      color: '#ffffff',
                    }}
                  >
                    {work.start}~{work.end}
                  </div>
                </Badge>
              ))}
          </div>
          {single && <CoordMap target={single} setInfo={setSingle} />}
        </div>
      )}
    </MainContentWrap>
  );
}
